/**
 * 🔒 useDataScope — React hook for hierarchical data visibility
 *
 * Automatically resolves the current user's data scope based on their
 * role_type in user_organizations. No manual toggles needed.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import {
  fetchUserScopeProfile,
  getUserScope,
  applyScopeFilter,
  type DataScope,
  type RoleType,
  type UserScopeProfile,
} from '../services/scopeService';

export interface UseDataScopeResult {
  scope: DataScope | null;
  roleType: RoleType;
  profile: UserScopeProfile | null;
  isLoading: boolean;
  applyScopeFilter: <T>(query: T, userColumn?: string, departmentColumn?: string) => T;
}

export function useDataScope(): UseDataScopeResult {
  const { user } = useAuth();
  const organizationId = useOrganizationId();
  const userId = user?.id;

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-scope-profile', userId, organizationId],
    queryFn: () => fetchUserScopeProfile(userId!, organizationId!),
    enabled: !!userId && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });

  const { data: scope, isLoading: scopeLoading } = useQuery({
    queryKey: ['user-data-scope', userId, organizationId, profile?.role_type],
    queryFn: () => getUserScope(userId!, organizationId!, profile!),
    enabled: !!userId && !!organizationId && !!profile,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = profileLoading || scopeLoading;
  const roleType = profile?.role_type ?? 'operational';

  const scopeFilter = useMemo(() => {
    return <T>(query: T, userColumn?: string, departmentColumn?: string): T => {
      if (!scope) return query;
      return applyScopeFilter(query, scope, userColumn, departmentColumn);
    };
  }, [scope]);

  return {
    scope: scope ?? null,
    roleType,
    profile: profile ?? null,
    isLoading,
    applyScopeFilter: scopeFilter,
  };
}
